/**
 * Ant Design Theme Configuration
 * Based on Figma Design System - Vasit Admin
 * Supports both Light and Dark themes
 */

import type { ThemeConfig } from 'antd';
import { theme as antdTheme } from 'antd';
import { antdTypographyToken, fontFamily } from './typography';
import { gray, brand, error, warning, success, blue, white, lightTheme } from './colors';

// Brand color from Figma (using brand palette)
export const brandColor = brand[500]; // #465FFF
export const brandColorLight = brand[50]; // #ECF3FF

// Sidebar colors from Figma (Light theme)
export const lightSidebarColors = {
  background: '#ffffff',
  itemText: gray[700], // #344054
  itemTextHover: gray[700], // #344054
  itemTextActive: brandColor, // #465fff (brand)
  itemBgActive: brandColorLight, // #ecf3ff
  itemBgHover: gray[100], // #F9FAFB
  groupTitle: gray[400], // #98A2B3
  iconColor: gray[500], // #667085
  border: gray[200], // #E4E7EC
} as const;

// Legacy export for backward compatibility
export const sidebarColors = lightSidebarColors;

/**
 * Base theme tokens shared between light and dark themes
 */
const baseTokens = {
  // Typography
  ...antdTypographyToken,

  // Border Radius
  borderRadius: 8,
  borderRadiusLG: 12,
  borderRadiusSM: 6,
};

/**
 * Base component styles shared between themes
 */
const baseComponentStyles = {
  Typography: {
    fontFamilyCode: fontFamily.primary,
    // Note: margin-bottom: 0 is applied globally via CSS in app/styles/index.css
    // to avoid inline className='mb-0!' usage
  },
  Pagination: {
    // Item size
    itemSize: 40,
    itemSizeSM: 32,

    // Border radius
    borderRadius: 8,

    // Font
    fontSize: 14,
    fontWeightStrong: 500,

    // Spacing
    marginXS: 4,
    marginXXS: 4,

    // Mini size disabled
    miniOptionsSizeChangerTop: 0,
  },
  Table: {
    // Header row height: 43px (43 - 14 font = 29 / 2 в‰€ 14.5 padding)
    // Body row height: 48px (48 - 14 font = 34 / 2 = 17 padding)
    cellPaddingBlock: 17, // Body row: 48px height
    cellPaddingBlockMD: 14,
    cellPaddingBlockSM: 10,
    cellPaddingInline: 16,
    cellPaddingInlineMD: 12,
    cellPaddingInlineSM: 8,

    // Font
    cellFontSize: 14,
    cellFontSizeMD: 14,
    cellFontSizeSM: 12,

    // Header specific
    headerSplitColor: 'transparent',
    headerBorderRadius: 0,

    // Selection
    selectionColumnWidth: 48,

    // Border radius
    borderRadius: 20,
    borderRadiusLG: 12,
  },
  Button: {
    // Size tokens
    controlHeight: 40, // Default (middle) size
    controlHeightLG: 48, // Large size
    controlHeightSM: 32, // Small size
    paddingContentHorizontal: 16,
    paddingContentHorizontalLG: 20,
    paddingContentHorizontalSM: 12,

    // Font sizes
    fontSize: 14,
    fontSizeLG: 16,
    fontSizeSM: 14,
    fontWeight: 500,

    // Border radius
    borderRadius: 8,
    borderRadiusLG: 10,
    borderRadiusSM: 6,

    // Primary button colors
    colorPrimary: brand[500],
    colorPrimaryHover: brand[600],
    colorPrimaryActive: brand[700],
    colorPrimaryBorder: brand[500],
    primaryShadow: 'none',

    // Default button
    defaultBorderColor: gray[300],
    defaultColor: gray[700],
    defaultHoverBorderColor: brand[300],
    defaultHoverColor: brand[600],
    defaultActiveBorderColor: brand[500],
    defaultActiveColor: brand[700],

    // Danger button
    colorError: error[500],
    colorErrorHover: error[600],
    colorErrorActive: error[700],
    dangerShadow: 'none',
  },
  Menu: {
    itemBg: 'transparent',
    subMenuItemBg: 'transparent',
    subMenuItemBorderRadius: 8,
    groupTitleFontSize: 12,
    itemHeight: 40,
    iconSize: 20,
    collapsedIconSize: 20,
    itemMarginInline: 0,
    itemMarginBlock: 4,
    itemPaddingInline: 12,
    fontSize: 14,
    itemBorderRadius: 8,
    iconMarginInlineEnd: 12,
    collapsedWidth: 52,
  },
  Layout: {
    headerPadding: '0 24px',
    headerHeight: 64,
  },
  Form: {
    labelFontSize: 14,
    verticalLabelPadding: '0 0 6px 0',
    // Note: margin-bottom: 0 is applied globally via CSS in app/styles/index.css
    // to avoid inline className='mb-0!' usage
  },
};

/**
 * Light Theme Configuration
 */
export const lightThemeConfig: ThemeConfig = {
  cssVar: {},
  hashed: false,
  algorithm: antdTheme.defaultAlgorithm,

  token: {
    ...baseTokens,
    colorPrimary: brand[500],
    colorInfo: blue[500],
    colorSuccess: success[500],
    colorWarning: warning[500],
    colorError: error[500],
    colorText: lightTheme.text.primary,
    colorTextSecondary: lightTheme.text.secondary,
    colorTextTertiary: lightTheme.text.tertiary,
    colorTextQuaternary: gray[300],
    colorBorder: lightTheme.border.primary,
    colorBorderSecondary: lightTheme.border.secondary,
    colorBgContainer: lightTheme.bg.primary,
    colorBgLayout: lightTheme.bg.secondary,
    colorBgElevated: lightTheme.bg.card,
  },

  components: {
    ...baseComponentStyles,
    Tabs: {
      // Global "segmented/pill" tabs look is handled via CSS in app/styles/index.css
      // These tokens keep typography/colors consistent.
      titleFontSize: 14,
      itemColor: lightTheme.text.secondary,
      itemHoverColor: lightTheme.text.primary,
      itemSelectedColor: lightTheme.text.primary,
      inkBarColor: 'transparent',
    },
    Menu: {
      ...baseComponentStyles.Menu,
      itemColor: lightSidebarColors.itemText,
      itemHoverColor: lightSidebarColors.itemTextHover,
      itemSelectedColor: lightSidebarColors.itemTextActive,
      itemSelectedBg: lightSidebarColors.itemBgActive,
      itemHoverBg: lightSidebarColors.itemBgHover,
      itemActiveBg: lightSidebarColors.itemBgHover,
      groupTitleColor: lightSidebarColors.groupTitle,
    },
    Layout: {
      ...baseComponentStyles.Layout,
      siderBg: lightSidebarColors.background,
      bodyBg: lightTheme.bg.secondary,
      headerBg: lightTheme.bg.primary,
    },
    Input: {
      // Size tokens
      controlHeight: 40, // Default (middle) size - 40px
      controlHeightLG: 48, // Large size - 48px
      controlHeightSM: 32, // Small size - 32px
      paddingBlock: 8,
      paddingBlockLG: 12,
      paddingBlockSM: 4,
      paddingInline: 12,
      paddingInlineLG: 16,
      paddingInlineSM: 8,

      // Font sizes
      fontSize: 14,
      fontSizeLG: 16,
      fontSizeSM: 14,

      // States - Light theme
      colorBgContainer: lightTheme.bg.input, // #FFFFFF
      colorBorder: lightTheme.border.secondary, // #D0D5DD
      hoverBorderColor: gray[400], // #98A2B3
      activeBorderColor: brand[300], // #9CB9FF (focus)

      // Text
      colorText: lightTheme.text.primary,
      colorTextPlaceholder: lightTheme.text.placeholder,
      colorTextDisabled: lightTheme.text.disabled,
      colorBgContainerDisabled: gray[50],
    },
    InputNumber: {
      // Size tokens (same as Input)
      controlHeight: 40, // Default (middle) size - 40px
      controlHeightLG: 48, // Large size - 48px
      controlHeightSM: 32, // Small size - 32px
      paddingBlock: 8,
      paddingBlockLG: 12,
      paddingBlockSM: 4,
      paddingInline: 12,
      paddingInlineLG: 16,
      paddingInlineSM: 8,

      // Font sizes
      fontSize: 14,
      fontSizeLG: 16,
      fontSizeSM: 14,

      // Border radius
      borderRadius: 8,
      borderRadiusLG: 10,
      borderRadiusSM: 6,

      // States - Light theme
      colorBgContainer: lightTheme.bg.input, // #FFFFFF
      colorBorder: lightTheme.border.secondary, // #D0D5DD
      hoverBorderColor: gray[400], // #98A2B3
      activeBorderColor: brand[300], // #9CB9FF (focus)

      // Text
      colorText: lightTheme.text.primary,
      colorTextPlaceholder: lightTheme.text.placeholder,
      colorTextDisabled: lightTheme.text.disabled,
      colorBgContainerDisabled: gray[50],
    },
    Form: {
      ...baseComponentStyles.Form,
      labelColor: lightTheme.text.label,
    },
    Card: {
      colorBgContainer: lightTheme.bg.card,
      colorBorderSecondary: lightTheme.border.primary,
    },
    Table: {
      ...baseComponentStyles.Table,
      // Header
      headerBg: lightTheme.bg.secondary, // #F9FAFB - matches global CSS
      headerColor: lightTheme.text.label, // #344054
      headerSplitColor: gray[200], // #E4E7EC - vertical border between header cells
      headerBorderRadius: 0, // Handled by global CSS

      // Body
      colorBgContainer: 'transparent',
      borderColor: gray[200], // #E4E7EC - all borders

      // Border radius
      borderRadius: 12, // var(--radius-lg)
      borderRadiusLG: 12,

      // Row - using brand light for hover (matches global CSS)
      rowHoverBg: brand[50], // #ECF3FF - brand light color
      rowSelectedBg: brand[50], // #ECF3FF
      rowSelectedHoverBg: brand[100], // #DDE9FF
      rowExpandedBg: gray[25], // #FCFCFD

      // Footer
      footerBg: 'transparent',
      footerColor: gray[500], // #667085

      // Sorter & Filter
      headerSortActiveBg: gray[50],
      headerSortHoverBg: gray[50],
      headerFilterHoverBg: gray[50],
      filterDropdownBg: white[100],
      fixedHeaderSortActiveBg: gray[50],
    },
    Select: {
      // Size tokens (same as Input)
      controlHeight: 40,
      controlHeightLG: 48,
      controlHeightSM: 32,

      // Colors
      colorBgContainer: lightTheme.bg.input,
      colorBorder: lightTheme.border.secondary,
      colorBgElevated: lightTheme.bg.card,

      // States
      hoverBorderColor: gray[400],
      activeBorderColor: brand[300],
    },
    ColorPicker: {
      // Size tokens (same as Input)
      controlHeight: 40,
      controlHeightLG: 48,
      controlHeightSM: 32,

      // Colors
      colorBgContainer: lightTheme.bg.input,
      colorBorder: lightTheme.border.secondary,
    },
    DatePicker: {
      // Size tokens (same as Input)
      controlHeight: 40,
      controlHeightLG: 48,
      controlHeightSM: 32,

      // Colors
      colorBgContainer: lightTheme.bg.input,
      colorBorder: lightTheme.border.secondary,
    },
    Checkbox: {
      colorPrimary: brand[500],
      colorPrimaryHover: brand[600],
      colorPrimaryActive: brand[700],
      colorBgContainer: lightTheme.bg.input,
      colorBorder: lightTheme.border.secondary,
      colorBgContainerDisabled: gray[50],
      colorText: lightTheme.text.primary,
      colorTextDisabled: lightTheme.text.disabled,
    },
    Switch: {
      // Pill shape
      borderRadius: 999,
      // Track
      colorPrimary: brand[500],
      colorPrimaryHover: brand[600],
      colorPrimaryActive: brand[700],
      // Text/icons on track
      colorText: lightTheme.text.inverse,
      colorTextTertiary: lightTheme.text.tertiary,
    },
    Radio: {
      borderRadiusSM: 999, // Full circle
      colorPrimary: brand[500],
      colorPrimaryHover: brand[600],
      colorPrimaryActive: brand[700],
      colorBgContainer: lightTheme.bg.input,
      colorBorder: lightTheme.border.secondary,
      colorBgContainerDisabled: gray[50],
      colorText: lightTheme.text.primary,
      colorTextDisabled: lightTheme.text.disabled,
    },
    Modal: {
      contentBg: lightTheme.bg.card,
      headerBg: lightTheme.bg.card,
    },
    Dropdown: {
      colorBgElevated: lightTheme.bg.card,
    },
    Pagination: {
      ...baseComponentStyles.Pagination,
      // Item colors
      itemBg: 'transparent',
      itemActiveBg: brand[500],
      itemActiveBgDisabled: gray[200],
      itemActiveColorDisabled: gray[400],
      itemInputBg: lightTheme.bg.input,

      // Text colors
      colorText: gray[500],
      colorTextDisabled: gray[300],
      colorPrimary: '#ffffff', // Active item text - white
      colorPrimaryHover: '#ffffff', // Hover text color - white
      colorBgTextHover: brand[500], // Hover background - same as active
      colorBgTextActive: brand[500], // Active background
    },
  },
};

export const theme: ThemeConfig = lightThemeConfig;
